import { call, put, select, takeEvery } from "redux-saga/effects";
import {
    NOTIFICATION_MESSAGE_ALERT_EVENTS,
    NOTIFICATION_MESSAGE_DEMO_LIMITS_EVENTS,
    NOTIFICATION_MESSAGE_HINT_EVENTS,
    NOTIFICATION_MESSAGE_LEADERBOARD_EVENTS,
    NOTIFICATION_MESSAGE_MARKET_EVENTS,
    NOTIFICATION_MESSAGE_POSITION_EVENTS,
    NOTIFICATION_MESSAGE_PROMOCODE_EVENTS,
    NOTIFICATION_MESSAGE_REFERRAL_EVENTS,
    NOTIFICATION_MESSAGE_TUTORIALS_EVENTS,
    NOTIFICATIONS_MESSAGE_TYPES,
} from "./notificationsConstants";
import {
    INotificationMessageDemoLimitsSendOrdersLeftIncome,
    INotificationMessageDeposit,
    INotificationMessageHints,
    INotificationMessageIncomingAlert,
    INotificationMessageIncomingMarket,
    INotificationMessageLeaderboard,
    INotificationMessagePosition,
    INotificationMessagePromocode,
    INotificationMessageReferralReceivedReward,
    INotificationMessageTutorials
} from "./notifications.t";
import { getAppFeatures, getAppUser, getUserVerify } from "../../app/appSelectors";
import { STATE_TX } from "../Account/components/TransactionHistory/transactionHistoryConstants";
import {
    appInitAction,
    loadAccountHistorySuccess,
    sendFacebookEventAction,
    setRequestFeedbackAction,
    updateAccountAction,
    userUpdateSuccess
} from "../../app/appActions";
import { PROMOCODE_DEFAULT_LIST } from "../Bonus/bonusesConstants";
import { accountGetPortfolioAction } from "../Account/accountActions";
import { setBonusPromocodeAction } from "../Bonus/bonusesActions";
import {
    getNotificationsWebsocketInstanceSelector,
    getNotificationsWsSendMessageFn,
    getShowedMessage
} from "./notificationsSelectors";
import timeout from "../../utils/timeout";
import {
    addNotificationToQueueAction,
    receiveNotificationMessageAction,
    sendNotificationMessageAction,
    setNotificationMessageAction,
    showNotificationFromQueueAction,
} from "./notificationsReducer";
import { IUserAchievementSummary } from "../Achievements/achievements.t";
import { ACHIEVEMENT_STATUS } from "../Achievements/achievementsConstants";
import { updateAchievementAction } from "../Achievements/achievementsReducer";
import { IAccountCode } from "../../app/types/appReducer.t";
import { checkIsTutorialsAvailableSaga } from "../Tutorials/tutorialsSaga";
import {
    deleteTutorialItemAction,
    loadTutorialsListAction,
    updateTutorialItemAction
} from "../Tutorials/tutorialsReducer";
import { makeSelectTutorialsItemByName } from "../Tutorials/tutorialsSelectors";
import { pushGtmEventAction } from "../GoogleTagManager/googleTagManagerActions";
import { GTM_EVENTS } from "../GoogleTagManager/googleTagManagerConstants";
import { FEATURES } from "../../app/appConstants";
import { alertsSlice } from "../Alerts/alertsReducer";
import { locationSlice } from "../Location/locationReducer";
import { LOCATION_NAMES } from "../Location/locationConstants";
import { userSlice } from "../../app/userReducer";
import { getAccountHistory } from "../Account/accountSelectors";
import { IHistory, IHistoryResponse } from "../Account/types/accountActions.t";
import { getLiteTradeMarketCollectionSuccess } from "../LiteTrade/liteTradeActions";
import { FB_EVENTS } from "../../utils/sendFacebookEvent";
import { userHintsSlice } from "../UserHints/userHintsReducer";
import { demoLimitsSlice } from "../DemoLimits/demoLimitsReducer";
import { referralSlice } from "../Referral/referralReducer";
import { REFERRAL_DISPLAY_FOR_TYPE } from "../Referral/referralConstants";
import { leaderboardSlice } from "../Leaderboard/leaderboardReducer";

function* showNotificationFromQueueSaga() {
    const msg = yield select(getShowedMessage);
    yield put(setNotificationMessageAction(msg));
    yield put(locationSlice.actions.addLocationAction({
        name: msg.type,
    }));
}

function* handleDepositNotificationMessageSaga({ payload }: { payload: INotificationMessageDeposit }) {
    const user = yield select(getAppUser);
    if (payload.params.state === STATE_TX.COMPLETED) {
        if (!user.buyer) {
            yield put(appInitAction());
        }
        yield put(pushGtmEventAction({ event: GTM_EVENTS.DEPOSIT_COMPLETED }));
        yield put(sendFacebookEventAction({
            event: FB_EVENTS.PAYMENT_DEPOSIT,
            eventID: `deposit_completed_${user.uid}`,
            data: {
                currency: payload.params.currency,
                value: parseFloat(payload.params.amount),
            },
        }));
    }
}

function* handlePromocodeNotificationMessageSaga({ payload }: { payload: INotificationMessagePromocode }) {
    const promocodeName = payload.params?.payload?.promocode;
    if (
        payload.params.event === NOTIFICATION_MESSAGE_PROMOCODE_EVENTS.ADD_PROMOCODE && promocodeName &&
        !PROMOCODE_DEFAULT_LIST.includes(promocodeName)
    ) {
        yield put(accountGetPortfolioAction());
        yield put(setBonusPromocodeAction(promocodeName));
    }
}

function* handleSendNotificationMessageSaga({ payload }: ReturnType<typeof sendNotificationMessageAction>) {
    const sendNotificationWsFn = yield select(getNotificationsWsSendMessageFn);
    if (sendNotificationWsFn) {
        sendNotificationWsFn(JSON.stringify(payload));
    }
}

function* handleTutorialsNotificationMessageSaga({ payload }: { payload: INotificationMessageTutorials }) {
    const isTutorialsAllowed = yield call(checkIsTutorialsAvailableSaga);
    if (!isTutorialsAllowed) return;

    switch (payload.params.event) {
        case NOTIFICATION_MESSAGE_TUTORIALS_EVENTS.TUTORIAL_CREATED:
        case NOTIFICATION_MESSAGE_TUTORIALS_EVENTS.POSITIONS_UPDATED:
            yield put(loadTutorialsListAction());
            break;
        case NOTIFICATION_MESSAGE_TUTORIALS_EVENTS.TUTORIAL_UPDATED:
            const updatedTutorialParams = payload.params.payload;
            const currentTutorial = yield select(makeSelectTutorialsItemByName(updatedTutorialParams.name));
            if (!currentTutorial) {
                yield put(loadTutorialsListAction());
            } else if (!updatedTutorialParams.active) {
                yield put(deleteTutorialItemAction(updatedTutorialParams.name));
            } else {
                yield put(updateTutorialItemAction(updatedTutorialParams));
            }
            break;
        case NOTIFICATION_MESSAGE_TUTORIALS_EVENTS.TUTORIAL_DELETED:
            yield put(deleteTutorialItemAction(payload.params.payload.name));
            break;
        default:
            break;
    }
}

function* handleReceiveNotificationMessageAlertSaga({ payload }: { payload: INotificationMessageIncomingAlert }) {
    const features = yield select(getAppFeatures);
    if (!features[FEATURES.ALERTS]) {
        return;
    }

    switch (payload.params.event) {
        case NOTIFICATION_MESSAGE_ALERT_EVENTS.TRIGGERED_ALERT:
            yield put(alertsSlice.actions.handleTriggeredAlertAction(payload.params.payload));
            break;
        default:
            break;
    }
}

function* handleUserStateNotificationMessageSaga({ payload }) {
    yield put(locationSlice.actions.addLocationAction({
        name: LOCATION_NAMES.USER_STATE_INFORMATION,
    }))
}

function* handleReceivedRewardNotificationMessageSaga(payload: INotificationMessageReferralReceivedReward) {
    if (payload.params.event === NOTIFICATION_MESSAGE_REFERRAL_EVENTS.RECEIVED_REWARD) {
        yield put(referralSlice.actions.setReferralNotifyReceivedReward(payload.params.payload));
        yield put(locationSlice.actions.addLocationAction({
            name: LOCATION_NAMES.REFERRAL_RECEIVED_REWARD,
        }))

        const data = payload.params.payload;

        if (data.type === REFERRAL_DISPLAY_FOR_TYPE.REFERRER) {
            yield put(referralSlice.actions.getReferrerRewardBalanceAction());
        }
    }
}

function* handleLeaderboardNotificationMessageSaga(payload: INotificationMessageLeaderboard) {
    switch (payload.params.event) {
        case NOTIFICATION_MESSAGE_LEADERBOARD_EVENTS.LEAGUE_PARAMS_UPDATED:
            yield put(leaderboardSlice.actions.setLeaderboardNotifyLeague(payload.params.payload));
            yield put(locationSlice.actions.addLocationAction({ name: LOCATION_NAMES.LEADERBOARD_NOTIFICATION_LEAGUE }))
            break;
    }
}

function* handlePositionNotificationMessageSaga({ payload }: { payload: INotificationMessagePosition }) {
    switch (payload.params.event) {
        case NOTIFICATION_MESSAGE_POSITION_EVENTS.CLOSED_POSITION:
            const position = payload.params.payload;
            const accountHistory = (yield select(getAccountHistory)) as IHistory;
            const { positions } = accountHistory;
            const isPositionAlreadyExist = positions.some((item) => item.position_code === position.position_code);
            if (isPositionAlreadyExist) return;
            const data: IHistoryResponse = {
                positions: [...positions, position],
            };
            yield put(loadAccountHistorySuccess(data));
            break;
        default:
            break;
    }
}

function* handleMarketNotificationMessageSaga({ payload }: { payload: INotificationMessageIncomingMarket }) {
    switch (payload.params.event) {
        case NOTIFICATION_MESSAGE_MARKET_EVENTS.COLLECTION_UPDATED:
            yield put(getLiteTradeMarketCollectionSuccess(payload.params.payload));
            break;
    }
}

function* handleHintsNotificationMessageSaga({ payload }: { payload: INotificationMessageHints }) {
    switch (payload.params.event) {
        case NOTIFICATION_MESSAGE_HINT_EVENTS.GET_USER_HINTS:
            yield put(userHintsSlice.actions.loadUserHintsSuccess(payload.params.payload));
            break;
        case NOTIFICATION_MESSAGE_HINT_EVENTS.ADD_USER_HINT:
            yield put(userHintsSlice.actions.loadAddUserHintSuccess(payload.params.payload));
            break;
    }
}

function* handleDemoLimitsNotificationMessageSaga({ payload }: {
    payload: INotificationMessageDemoLimitsSendOrdersLeftIncome
}) {
    switch (payload.params.event) {
        case NOTIFICATION_MESSAGE_DEMO_LIMITS_EVENTS.GET_ORDERS_LEFT:
            yield put(demoLimitsSlice.actions.getFreeOrdersLeftSuccess(payload.params.payload));
            break;
    }
}

function* handleReceiveNotificationMessageSaga({ payload }: ReturnType<typeof receiveNotificationMessageAction>) {
    switch (payload.type) {
        case NOTIFICATIONS_MESSAGE_TYPES.DEPOSIT:
            yield put(setNotificationMessageAction(payload));
            yield call(handleDepositNotificationMessageSaga, { payload });
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.PROMOCODE:
            yield put(setNotificationMessageAction(payload));
            yield call(handlePromocodeNotificationMessageSaga, { payload });
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.ACHIEVEMENT:
            if (payload.params.event === ACHIEVEMENT_STATUS.PROCESS) {
                yield put(updateAchievementAction(payload.params.payload as unknown as IUserAchievementSummary));
            } else if (payload.params.event === ACHIEVEMENT_STATUS.COMPLETED) {
                yield call(timeout, 200); // Событие об ачивках может прийти раньше, чем от DX
                yield put(addNotificationToQueueAction(payload));
                yield put(updateAchievementAction(payload.params.payload as unknown as IUserAchievementSummary));
            }
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.LEADERBOARD:
            yield call(handleLeaderboardNotificationMessageSaga, payload);
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.REQUEST_FEEDBACK:
            yield put(addNotificationToQueueAction(payload));
            yield put(setRequestFeedbackAction(true));
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.RESPAWN_MOTIVATION:
            yield put(addNotificationToQueueAction(payload));
            yield put(userSlice.actions.setRespawnMotivation(payload.params));
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.REFERRAL:
            yield call(handleReceivedRewardNotificationMessageSaga, payload);
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.USER_STATE_INFORMATION:
            yield call(handleUserStateNotificationMessageSaga, { payload: payload.params });
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.ACCOUNT_UPDATES:
            yield put(updateAccountAction({
                ...payload.params.payload,
                name: payload.params.accountCode as IAccountCode,
            }));
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.WITHDRAWAL:
            yield put(setNotificationMessageAction(payload));
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.TUTORIALS:
            yield put(setNotificationMessageAction(payload));
            yield call(handleTutorialsNotificationMessageSaga, { payload });
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.USER_PARAMS_UPDATE:
            yield put(userUpdateSuccess(payload.params.payload));
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.USER_STATUS_UPDATE:
            yield put(appInitAction());
            yield put(locationSlice.actions.openLocationAction({ name: LOCATION_NAMES.STATUS_CONGRATULATIONS }));
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.VERIFY:
            const userVerify = yield select(getUserVerify);
            yield put(userUpdateSuccess({
                verify: {
                    ...userVerify,
                    ...payload.params.payload,
                },
            }));

            const features = yield select(getAppFeatures);

            if (features[FEATURES.REFERRAL]) {
                yield put(referralSlice.actions.getReferralAction());
            }
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.ALERT:
            yield call(handleReceiveNotificationMessageAlertSaga, { payload });
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.POSITION:
            yield call(handlePositionNotificationMessageSaga, { payload });
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.MARKET:
            yield call(handleMarketNotificationMessageSaga, { payload });
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.HINTS:
            yield call(handleHintsNotificationMessageSaga, { payload });
            break;
        case NOTIFICATIONS_MESSAGE_TYPES.DEMO_LIMITS:
            yield call(handleDemoLimitsNotificationMessageSaga, { payload });
            break;
        default:
            break;
    }
}

export function* closeNotificationsWebsocketForReconnectSaga() {
    const notificationsWebsocketInstance = yield select(getNotificationsWebsocketInstanceSelector);
    if (notificationsWebsocketInstance) {
        notificationsWebsocketInstance.close();
    }
}

export function* notificationsSagas() {
    yield takeEvery(showNotificationFromQueueAction, showNotificationFromQueueSaga);
    yield takeEvery(receiveNotificationMessageAction, handleReceiveNotificationMessageSaga);
    yield takeEvery(sendNotificationMessageAction, handleSendNotificationMessageSaga);
}